/* CHARM QUIZ ENGINE — SESSION 12, AMENDED IN SESSION 13. 5 Aug 2026

   REPLACES the regulation-percentage engine. The 5-25 pain score, the percentage, the
   ring and the four bands are gone. This engine runs the 13-question initiation ported
   from the Lovable build and returns a named Reflection Profile out of five modes.

   DETERMINISM CONTRACT, UNCHANGED AND STILL BINDING
   classify(answers) is pure. No randomness, no time, no storage, no network. Identical
   answers always produce an identical page. The result is additionally fingerprinted
   and cached in sessionStorage so a refresh on the result screen never reclassifies.

   HOW THE RESULT IS DECIDED
     structured 70%  five questions only: resonant_patterns, dream_life, state_awareness,
                     life_flow, reflection_commitment
     text       30%  five long-text answers, keyword hits on a sqrt curve so one loaded
                     sentence cannot decide the outcome
   Each half is normalised to its own strongest mode = 1.0 BEFORE blending. Any mode
   within tieThreshold of the winner goes to a four-stage tie break. All weights, word
   lists and the tie-break order live in snippets/charm-quiz-questions.liquid. Nothing
   is hard coded here.

   STEP KINDS
     story       educational break, optional image, numbered paragraphs
     short_text  one line
     date        native date input
     long_text   textarea
     single      option cards, autoAdvance moves on immediately
     multi       option cards, many allowed, Continue required, exclusive clears rest

   RESULT SCREEN
     Single centred column that scrolls. root carries data-cq-result while it is up.
     Sections, in order: hero, pattern, what you revealed, the mode, why Charm, voice,
     product CTA, final reflection. Every string falls back to a shipped default, so a
     section that has not been configured still renders.

   SAFETY. Nothing on the result screen may claim voice analysis, detection, diagnosis
   or certainty. The copy in the questions snippet is written to that rule. If you add
   copy, keep it interpretive.

   ============================================================
   S13 CHANGES. THREE OF THEM. ALL IN renderResults AND ITS WIRING.
   ============================================================
   1  THE DUPLICATE HEADING IS GONE. It rendered 'NAME, YOUR REFLECTION IS READY' and
      then 'YOUR REFLECTION PROFILE' immediately under it, two headings carrying the
      same message. Now: the name alone on top in the profile-title gradient, then
      'YOUR REFLECTION PROFILE', then the profile name. His instruction verbatim:
      'use only one name on top in centered same kind of accent color as The Emerging
      Self and then under it YOUR REFLECTION PROFILE'.

   2  THE 30 DAY REFLECTION CAPTURE, render30(). It sits directly under .cq__hint in
      the hero, which is exactly where he placed it. The button is a bare .cq__cta so
      it inherits the measured spec off the other buttons rather than declaring new
      numbers. The box is inline, not a modal.

      READ THIS BEFORE YOU TRUST IT WITH A REAL SIGNUP. There is no backend yet.
      Submitting fires charm:reflection:signup on the root and POSTs to
      DATA.results.reflectEndpoint, falling back to DATA.lead.endpoint. BOTH ARE
      CURRENTLY EMPTY, so today the email is captured into an event that nothing is
      listening to and then lost. The screen tells the reader it worked. That is a
      real liability and it is written up as P0 in the S13 handoff. Do not run traffic
      at this page until the endpoint exists.

   3  CONTINUE TO CHARM IS A BUTTON AND RETAKE IS A LINE UNDER IT. They used to share
      one .cq__links row as two text links. His words: 'one final reflection should
      have a button that says continue to charm ... and under it a minimal line retake
      the quiz'.
*/
(function () {
  'use strict';

  var CACHE_KEY = 'charm_reflection_result';

  function boot(root) {
    if (root.dataset.charmQuizBooted) return;
    root.dataset.charmQuizBooted = '1';

    var DATA = JSON.parse(root.querySelector('[data-charm-quiz-data]').textContent);
    var STEPS = DATA.steps;
    var MODES = DATA.modes;
    var SC = DATA.scoring;
    var KEYS = SC.modeKeys;

    var view = root.querySelector('[data-cq-view]');
    var intro = root.querySelector('[data-cq-intro]');
    var answers = {};
    var index = -1;
    var lead = null;

    /* Progress counts questions only. A story break is not something the reader is
       asked to complete, and counting them makes the bar stall on a screen with no
       input. Plus one for the details screen. */
    var COUNTED = STEPS.filter(function (s) { return s.kind !== 'story'; }).length + 1;

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function arr(v) { return Array.isArray(v) ? v : (typeof v === 'string' && v ? [v] : []); }
    function low(v) { return typeof v === 'string' ? v.toLowerCase() : ''; }

    /* ---------- the pure part ---------- */

    function zero() {
      var s = {};
      KEYS.forEach(function (k) { s[k] = 0; });
      return s;
    }
    function add(target, delta) {
      if (!delta) return;
      KEYS.forEach(function (k) { if (delta[k]) target[k] += delta[k]; });
    }
    /* Normalising each half to its own maximum is what keeps the 70/30 split honest.
       Without it the structured half, which has far more raw headroom, would swamp the
       text half no matter what the reader wrote. */
    function normalise(s) {
      var max = 0;
      KEYS.forEach(function (k) { if (s[k] > max) max = s[k]; });
      var out = zero();
      if (max <= 0) return out;
      KEYS.forEach(function (k) { out[k] = Math.round((s[k] / max) * 100) / 100; });
      return out;
    }

    function scoreStructured(a) {
      var s = zero();
      var sig = SC.signals;
      arr(a.resonant_patterns).forEach(function (id) { add(s, sig.resonant_patterns[id]); });
      arr(a.dream_life).forEach(function (id) { add(s, sig.dream_life[id]); });
      add(s, sig.state_awareness[a.state_awareness]);
      add(s, sig.life_flow[a.life_flow]);
      add(s, sig.reflection_commitment[a.reflection_commitment]);
      return normalise(s);
    }

    function scoreText(a) {
      var s = zero();
      SC.textFields.forEach(function (f) {
        var text = low(a[f.id]);
        if (!text) return;
        KEYS.forEach(function (mode) {
          var hits = 0;
          SC.textSignals[mode].forEach(function (term) { if (text.indexOf(term) !== -1) hits += 1; });
          /* Diminishing returns. Ten hits is worth about three times one hit, not ten. */
          if (hits) s[mode] += Math.sqrt(hits) * f.weight;
        });
      });
      return normalise(s);
    }

    function termHits(text) {
      var s = zero();
      var t = low(text);
      if (!t) return s;
      KEYS.forEach(function (mode) {
        SC.textSignals[mode].forEach(function (term) { if (t.indexOf(term) !== -1) s[mode] += 1; });
      });
      return s;
    }

    /* Four stages, in the order set in the questions snippet. A stage that cannot
       separate the pool is skipped rather than allowed to collapse it. */
    function tieBreak(a, pool) {
      SC.tieBreak.forEach(function (stageId) {
        if (pool.length === 1) return;
        var s;
        if (stageId === 'resonant_patterns') {
          s = zero();
          arr(a.resonant_patterns).forEach(function (id) { add(s, SC.signals.resonant_patterns[id]); });
        } else if (stageId === 'state_awareness') {
          s = zero();
          add(s, SC.signals.state_awareness[a.state_awareness]);
        } else {
          s = termHits(a[stageId]);
        }
        var best = -Infinity;
        pool.forEach(function (m) { if (s[m] > best) best = s[m]; });
        var next = pool.filter(function (m) { return s[m] === best; });
        if (next.length && next.length < pool.length) pool = next;
      });
      return pool[0];
    }

    function classify(a) {
      var structured = scoreStructured(a);
      var text = scoreText(a);

      var combined = zero();
      KEYS.forEach(function (k) {
        combined[k] = Math.round((structured[k] * SC.structuredWeight + text[k] * SC.textWeight) * 1000) / 1000;
      });

      var max = 0;
      KEYS.forEach(function (k) { if (combined[k] > max) max = combined[k]; });

      var near = KEYS.filter(function (k) { return max - combined[k] <= SC.tieThreshold; });
      var mode = max === 0 ? SC.defaultMode : tieBreak(a, near.length ? near.slice() : KEYS.slice());

      var p = MODES[mode];
      return {
        mode: mode,
        profileTitle: p.profileTitle,
        modeName: p.modeName,
        scores: combined,
        structured: structured,
        text: text,
        explanation: max === 0
          ? 'Not enough signal in the responses; defaulted to ' + MODES[SC.defaultMode].modeName + '.'
          : 'Structured responses (' + Math.round(SC.structuredWeight * 100) + '%) and written responses (' +
            Math.round(SC.textWeight * 100) + '%) point most strongly toward ' + p.modeName +
            (near.length > 1 ? ', resolved from a close result using the blocked-response and pattern answers' : '') + '.'
      };
    }
    root.charmQuizClassify = classify;

    /* Same answers in, same profile out, across a refresh. The fingerprint is the
       answer set, so a changed answer correctly invalidates the cache. sessionStorage
       rather than local: a new visit should be a new reading. */
    function fingerprint(a) {
      try {
        return JSON.stringify(Object.keys(a).sort().map(function (k) { return [k, a[k]]; }));
      } catch (e) { return ''; }
    }
    function cachedClassify(a) {
      var fp = fingerprint(a);
      try {
        var raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          var stored = JSON.parse(raw);
          if (stored && stored.fingerprint === fp && stored.result && MODES[stored.result.mode]) return stored.result;
        }
      } catch (e) { /* storage disabled, fall through */ }
      var result = classify(a);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ fingerprint: fp, result: result })); } catch (e) {}
      return result;
    }

    /* ---------- chrome ---------- */

    function countedSoFar() {
      var n = 0;
      for (var i = 0; i <= index && i < STEPS.length; i++) if (STEPS[i].kind !== 'story') n++;
      if (index >= STEPS.length) n = COUNTED;
      return n;
    }
    function progressHtml() {
      var done = countedSoFar();
      var pct = Math.round((done / COUNTED) * 100);
      var left = Math.max(COUNTED - done, 0);
      var label = left === 1 ? '1 step remaining' : left + ' steps remaining';
      return '<div class="cq__prog"><div class="cq__prog-row"><span>' + label +
        '</span><span>' + pct + '%</span></div><div class="cq__prog-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct +
        '"><div class="cq__prog-fill" style="width:' + pct + '%"></div></div></div>';
    }
    function backHtml() { return index > 0 ? '<button type="button" class="cq__back" data-cq-back>Back</button>' : ''; }
    function arrow() { return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h15M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'; }
    function cta(label, attr) {
      return '<button type="button" class="cq__cta" ' + (attr || 'data-cq-next') + '><span>' + esc(label) + '</span>' + arrow() + '</button>';
    }
    function head(step) {
      return '<h2 class="cq__q" tabindex="-1" data-cq-focus>' + esc(step.question || step.title) + '</h2>' +
        (step.subtext ? '<p class="cq__sub">' + esc(step.subtext) + '</p>' : '');
    }

    /* ---------- renderers ---------- */

    function renderOptions(step) {
      var multi = step.kind === 'multi';
      var chosen = multi ? arr(answers[step.id]) : [answers[step.id]];
      var opts = step.options.map(function (o, n) {
        var on = chosen.indexOf(o.id) !== -1;
        return '<button type="button" class="cq__opt' + (on ? ' is-on' : '') + '" style="--i:' + n + '"' +
          ' role="' + (multi ? 'checkbox' : 'radio') + '" aria-checked="' + (on ? 'true' : 'false') + '"' +
          ' data-cq-opt="' + esc(o.id) + '"><span class="cq__dot" aria-hidden="true"></span>' +
          '<span class="cq__opt-label">' + esc(o.label) + '</span></button>';
      }).join('');

      var needsContinue = multi || step.autoAdvance === false;
      var ready = multi ? chosen.length > 0 : !!answers[step.id];
      var action = needsContinue
        ? cta(step.continueLabel || 'Continue', 'data-cq-next' + (ready ? '' : ' disabled'))
        : '';

      return progressHtml() + head(step) +
        '<div class="cq__opts" role="group">' + opts + '</div>' +
        (step.footnote ? '<p class="cq__note">' + esc(step.footnote) + '</p>' : '') +
        action + backHtml();
    }

    function renderInput(step) {
      var v = answers[step.id] || '';
      var field;
      if (step.kind === 'long_text') {
        field = '<textarea class="cq__input cq__input--area" data-cq-input rows="5" placeholder="' +
          esc(step.placeholder || '') + '">' + esc(v) + '</textarea>';
      } else if (step.kind === 'date') {
        field = '<input class="cq__input" data-cq-input type="date" value="' + esc(v) + '">';
      } else {
        field = '<input class="cq__input" data-cq-input type="text" autocomplete="given-name" placeholder="' +
          esc(step.placeholder || '') + '" value="' + esc(v) + '">';
      }
      var ready = step.required === false || String(v).trim() !== '';
      return progressHtml() + head(step) +
        '<div class="cq__inputwrap" style="--i:0">' + field + '</div>' +
        cta(step.continueLabel || 'Continue', 'data-cq-next' + (ready ? '' : ' disabled')) + backHtml();
    }

    function renderStory(step) {
      var img = step.image
        ? '<div class="cq__storyimg"><img src="' + esc(step.image) + '" alt="" loading="lazy"></div>' : '';
      var paras = step.paras.map(function (p, i) {
        return '<p class="cq__story-p" style="--i:' + i + '"><span class="cq__story-n">' + ('0' + (i + 1)) + '</span>' + esc(p) + '</p>';
      }).join('');
      return progressHtml() +
        '<h2 class="cq__q" tabindex="-1" data-cq-focus>' + esc(step.title) + '</h2>' +
        (step.lede ? '<p class="cq__lede">' + esc(step.lede) + '</p>' : '') +
        img +
        '<div class="cq__story">' + paras + '</div>' +
        cta('Continue') + backHtml();
    }

    function renderLead() {
      var L = DATA.lead || {};
      return progressHtml() +
        '<h2 class="cq__q" tabindex="-1" data-cq-focus>' + esc(L.title) + '</h2>' +
        '<p class="cq__sub">' + esc(L.body) + '</p>' +
        '<form class="cq__form" data-cq-form novalidate>' +
        '<label class="cq__field" style="--i:0"><span>Email address</span><input type="email" name="email" autocomplete="email" required></label>' +
        '<label class="cq__field" style="--i:1"><span>Phone <em>optional</em></span><input type="tel" name="phone" autocomplete="tel"></label>' +
        '<p class="cq__err" data-cq-err hidden></p>' +
        '<button type="submit" class="cq__cta"><span>' + esc(L.button) + '</span>' + arrow() + '</button>' +
        '<p class="cq__privacy">' + esc(L.privacy) + '</p></form>' + backHtml();
    }

    /* The analysing screen runs BEFORE the details are asked for, by request: it builds
       investment before the ask rather than after it. Purely presentational, the
       classification itself is instant and already cached. */
    function renderAnalysing() {
      var R = DATA.results || {};
      var lines = R.analysingSteps || [];
      return '<div class="cq__analyse">' +
        '<h2 class="cq__q" tabindex="-1" data-cq-focus>' + esc(R.analysingTitle || 'Preparing Your Charm Reflection') + '</h2>' +
        '<ul class="cq__steps">' + lines.map(function (t, i) {
          return '<li class="cq__step" data-i="' + i + '"><span class="cq__stepdot" aria-hidden="true"></span>' + esc(t) + '</li>';
        }).join('') + '</ul></div>';
    }

    function revealed(label, quote, interpretation, question) {
      if (!quote) return '';
      return '<div class="cq__rev">' +
        '<p class="cq__revlabel">' + esc(label) + '</p>' +
        '<blockquote class="cq__quote">' + esc(quote) + '</blockquote>' +
        '<p class="cq__revbody">' + esc(interpretation) + '</p>' +
        (question ? '<p class="cq__revq">' + esc(question) + '</p>' : '') +
        '</div>';
    }
    function rule() { return '<div class="cq__rule" aria-hidden="true"></div>'; }

    /* S13. THE 30 DAY REFLECTION CAPTURE.
       Three states in one wrapper: the button, the box, the confirmation. Only one is
       visible at a time and the transitions are driven by the click handler below
       rather than by re-rendering, because a repaint of the result screen would throw
       the reader back to the top of a very long page.

       The button carries NO data-cq-next. If you add it, clicking it will advance the
       flow past the result screen. */
    function render30() {
      var R = DATA.results || {};
      if (R.reflect30Enabled === false) return '';
      var pre = (lead && lead.email) ? lead.email : '';
      return '<div class="cq__r30" data-cq-r30>' +
        '<button type="button" class="cq__cta" data-cq-r30-open><span>' +
          esc(R.reflect30Label || 'Get your 30 day reflection') + '</span>' + arrow() + '</button>' +
        '<div class="cq__r30-box" data-cq-r30-box hidden>' +
          '<form class="cq__r30-form" data-cq-rform novalidate>' +
            '<label class="cq__r30-label" for="cq-r30-email">' +
              esc(R.reflect30Prompt || 'Where it should arrive') + '</label>' +
            '<div class="cq__r30-row">' +
              '<input class="cq__input cq__r30-input" id="cq-r30-email" type="email" name="email" ' +
                'autocomplete="email" placeholder="you@example.com" value="' + esc(pre) + '" required>' +
              '<button type="submit" class="cq__cta"><span>' +
                esc(R.reflect30Send || 'Begin the thirty days') + '</span>' + arrow() + '</button>' +
            '</div>' +
            '<p class="cq__err" data-cq-rerr hidden></p>' +
          '</form>' +
        '</div>' +
        '<div class="cq__r30-done" data-cq-r30-done hidden>' +
          '<p class="cq__r30-donetitle">' +
            esc(R.reflect30DoneTitle || 'Your thirty days begin.') + '</p>' +
          '<p class="cq__r30-donebody">' +
            esc(R.reflect30DoneBody || 'The first reflection reaches you tomorrow. One arrives each day after, written for the pattern you just named.') + '</p>' +
        '</div>' +
      '</div>';
    }

    function renderResults() {
      var r = cachedClassify(answers);
      var p = MODES[r.mode];
      var R = DATA.results || {};

      var first = String(answers.name || '').trim().split(' ')[0] || 'Friend';

      var pattern = [
        { label: 'YOUR STRENGTH',       title: p.strengthTitle,  body: p.strengthBody },
        { label: 'YOUR CHALLENGE',      title: p.challengeTitle, body: p.challengeBody },
        { label: 'YOUR NEXT EVOLUTION', title: p.evolutionTitle, body: p.evolutionBody }
      ].map(function (c) {
        return '<div class="cq__pcard">' +
          '<p class="cq__revlabel">' + esc(c.label) + '</p>' +
          '<h3 class="cq__ptitle">' + esc(c.title) + '</h3>' +
          '<p class="cq__pbody">' + esc(c.body) + '</p></div>';
      }).join('');

      var revealedBlocks =
        revealed('WHAT FEELS BLOCKED', answers.current_block, p.blockInterpretation) +
        revealed('WHAT YOU WISH OTHERS COULD HEAR', answers.unspoken_truth, p.unspokenInterpretation) +
        revealed('WHO YOU ARE BECOMING', answers.becoming_self, p.becomingInterpretation) +
        revealed('YOUR 90-DAY INTENTION', answers.ninety_day_intention, p.intentionInterpretation,
                 'Who must you consistently be while creating this?');

      var reflect = '';
      if (R.reflectLabel) {
        reflect = R.reflectUrl
          ? '<a class="cq__cta cq__reflect" href="' + esc(R.reflectUrl) + '"><span>' + esc(R.reflectLabel) + '</span>' + arrow() + '</a>'
          : '<button type="button" class="cq__cta cq__reflect"><span>' + esc(R.reflectLabel) + '</span>' + arrow() + '</button>';
      }

      /* S13. Continue to Charm is a real glass button and it goes to the homepage.
         Retake is a minimal line UNDER it, not a sibling in the same row. */
      var continueCta = '<a class="cq__cta cq__continue" href="' + esc(R.ctaUrl || '/') + '"><span>' +
        esc(R.ctaLabel || 'Continue to Charm') + '</span>' + arrow() + '</a>';
      var retake = '<button type="button" class="cq__textlink cq__retake" data-cq-retake>' +
        esc(R.retakeLabel || 'Retake the quiz') + '</button>';

      return '<div class="cq__result">' +

        '<section class="cq__hero">' +
          '<p class="cq__name">' + esc(first.toUpperCase()) + '</p>' +
          '<p class="cq__eyebrow cq__eyebrow--soft">YOUR REFLECTION PROFILE</p>' +
          '<h2 class="cq__title" tabindex="-1" data-cq-focus>' + esc(p.profileTitle) + '</h2>' +
          '<p class="cq__modeline">Associated Charm Mode: <span>' + esc(p.modeName) + '</span></p>' +
          '<div class="cq__introcopy">' + p.intro.map(function (l) { return '<p>' + esc(l) + '</p>'; }).join('') + '</div>' +
          '<p class="cq__hint">' + esc(R.coreAreasHint || 'Your answers point most strongly toward one of the five core areas Charm is designed to support.') + '</p>' +
          render30() +
        '</section>' +

        rule() +

        '<section class="cq__sec">' +
          '<h3 class="cq__sech">' + esc(R.patternTitle || 'A Quick Look at Your Pattern') + '</h3>' +
          '<div class="cq__pgrid">' + pattern + '</div>' +
        '</section>' +

        (revealedBlocks ? rule() +
          '<section class="cq__sec">' +
            '<h3 class="cq__sech">' + esc(R.revealedTitle || 'What You Revealed') + '</h3>' +
            revealedBlocks +
          '</section>' : '') +

        rule() +

        '<section class="cq__sec cq__sec--mid">' +
          '<h3 class="cq__sech">' + esc(R.coreAreasTitle || 'One of the Five Core Areas Charm Reflects') + '</h3>' +
          '<p class="cq__hint">Your answers point most strongly toward:</p>' +
          '<p class="cq__modename">' + esc(p.modeName) + '</p>' +
          '<div class="cq__body">' + p.modeExplanation.map(function (l) { return '<p>' + esc(l) + '</p>'; }).join('') + '</div>' +
          '<p class="cq__hint">' + esc(R.onboardingNote || 'Your full Charm onboarding will explore all five modes and create a more complete reflection of how you currently show up.') + '</p>' +
        '</section>' +

        rule() +

        '<section class="cq__sec">' +
          '<h3 class="cq__sech">' + esc(R.whyTitle || 'You Cannot Change a Pattern You Only Notice Afterwards') + '</h3>' +
          '<div class="cq__body">' +
            '<p>' + esc(R.whyBody1 || 'Charm is a wearable reflection designed to help you become more conscious of how you are showing up through your voice.') + '</p>' +
            '<p>' + esc(R.whyBody2 || 'It helps reveal recurring patterns in your expression, state and communication, giving you an opportunity to return to yourself while the moment is still unfolding.') + '</p>' +
            '<p class="cq__pull">' + esc(R.whyPull || 'Not hours later. Not after replaying the conversation. In the moment where another response is still available.') + '</p>' +
            '<p>' + esc(p.whyCharmParagraph) + '</p>' +
            '<p>' + esc(p.charmRelevance) + '</p>' +
          '</div>' +
        '</section>' +

        rule() +

        '<section class="cq__sec cq__sec--mid">' +
          '<h3 class="cq__sech">' + esc(R.shapingTitle || 'Your Voice Is Already Shaping What Comes Next') + '</h3>' +
          '<div class="cq__verse">' +
            '<p>Every conversation rehearses an identity.</p>' +
            '<p>Every repeated reaction strengthens a pattern.</p>' +
            '<p>Every moment of truthful expression makes the person you are becoming more real.</p>' +
            '<p class="cq__versegap">Charm does not decide who that person should be.</p>' +
            '<p>It helps you hear the person who is already trying to emerge.</p>' +
          '</div>' +
        '</section>' +

        '<section class="cq__sec cq__sec--mid cq__ctasec">' +
          (R.productImage ? '<img class="cq__prodimg" src="' + esc(R.productImage) + '" alt="" loading="lazy">' : '') +
          '<h3 class="cq__sech">' + esc(R.nextStepTitle || 'Ready to Continue Your Initiation?') + '</h3>' +
          '<p class="cq__body cq__body--centre">' + esc(R.nextStepBody || 'Discover how Charm becomes a private mirror for your state, your patterns and the future you are consciously authoring.') + '</p>' +
          reflect +
          '<p class="cq__privacy cq__privacy--centre">' + esc(R.privacyNote || 'Your answers remain private.') + '</p>' +
        '</section>' +

        rule() +

        '<section class="cq__sec cq__sec--mid">' +
          '<h3 class="cq__sech">' + esc(R.finalTitle || 'One Final Reflection') + '</h3>' +
          '<div class="cq__verse">' +
            '<p>' + esc(R.finalLine1 || 'You do not need to become someone else.') + '</p>' +
            '<p>' + esc(R.finalLine2 || 'You need a way to remember who you are before the moment passes.') + '</p>' +
          '</div>' +
          continueCta +
          '<p class="cq__links">' + retake + '</p>' +
        '</section>' +

      '</div>';
    }

    /* ---------- flow ---------- */

    var ANALYSE = STEPS.length;      /* analysing screen */
    var LEAD = STEPS.length + 1;     /* details */
    var RESULT = STEPS.length + 2;   /* reflection profile */

    function paint() {
      var html;
      if (index >= 0 && index < STEPS.length) {
        var step = STEPS[index];
        if (step.kind === 'story') html = renderStory(step);
        else if (step.kind === 'single' || step.kind === 'multi') html = renderOptions(step);
        else html = renderInput(step);
      } else if (index === ANALYSE) { html = renderAnalysing(); }
      else if (index === LEAD) { html = renderLead(); }
      else { html = renderResults(); }

      view.innerHTML = html;
      view.classList.remove('is-in');
      void view.offsetWidth;
      view.classList.add('is-in');

      var h = view.querySelector('[data-cq-focus]');
      if (h) h.focus({ preventScroll: true });

      if (index === ANALYSE) runAnalysing();
      if (index >= 0 && index < STEPS.length) wireInput(STEPS[index]);
    }

    /* The analysing sequence ticks its lines then moves on by itself. Reduced motion
       skips straight through rather than making somebody wait for a decoration. */
    function runAnalysing() {
      var lines = view.querySelectorAll('.cq__step');
      if (!lines.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setTimeout(function () { go(LEAD); }, 200);
        return;
      }
      var i = 0;
      lines[0].classList.add('is-active');
      var t = setInterval(function () {
        if (!document.body.contains(lines[0])) { clearInterval(t); return; }
        lines[i].classList.remove('is-active');
        lines[i].classList.add('is-done');
        i += 1;
        if (i >= lines.length) { clearInterval(t); setTimeout(function () { go(LEAD); }, 520); return; }
        lines[i].classList.add('is-active');
      }, 780);
    }

    /* Typing must not repaint the view or the caret is lost on every keystroke. The
       value goes straight to answers and only the Continue button is toggled. */
    function wireInput(step) {
      var el = view.querySelector('[data-cq-input]');
      if (!el) return;
      var btn = view.querySelector('[data-cq-next]');
      el.addEventListener('input', function () {
        answers[step.id] = el.value;
        if (btn) {
          var ok = step.required === false || el.value.trim() !== '';
          if (ok) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', '');
        }
      });
      if (step.kind === 'short_text' || step.kind === 'date') {
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); if (btn && !btn.hasAttribute('disabled')) go(index + 1); }
        });
      }
    }

    function go(i) {
      index = i;
      if (index === -1) {
        intro.hidden = false; view.hidden = true; view.innerHTML = '';
        root.removeAttribute('data-cq-running');
        root.removeAttribute('data-cq-result');
        root.removeAttribute('data-cq-analysing');
        return;
      }
      intro.hidden = true; view.hidden = false;
      root.setAttribute('data-cq-running', '');
      if (index === RESULT) root.setAttribute('data-cq-result', '');
      else root.removeAttribute('data-cq-result');
      if (index === ANALYSE) root.setAttribute('data-cq-analysing', '');
      else root.removeAttribute('data-cq-analysing');
      paint();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function back() {
      var next = index - 1;
      /* Never land back on the analysing screen, it would re-run and re-advance. */
      if (next === ANALYSE) next -= 1;
      go(Math.max(next, 0));
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-cq-start]')) { e.preventDefault(); go(0); return; }

      /* S13. The 30 day box opens in place. Checked BEFORE data-cq-next so a future
         edit that adds that attribute to the button cannot silently advance the flow. */
      var r30open = e.target.closest('[data-cq-r30-open]');
      if (r30open && view.contains(r30open)) {
        e.preventDefault();
        var wrap30 = r30open.closest('[data-cq-r30]');
        /* Inline style, not the hidden attribute: .cq__cta declares a display value and
           would win over [hidden]. */
        r30open.style.display = 'none';
        var box30 = wrap30.querySelector('[data-cq-r30-box]');
        box30.hidden = false;
        var inp30 = box30.querySelector('input[type="email"]');
        if (inp30) inp30.focus();
        return;
      }

      var o = e.target.closest('[data-cq-opt]');
      if (o && view.contains(o)) {
        var step = STEPS[index];
        var id = o.getAttribute('data-cq-opt');

        if (step.kind === 'multi') {
          var cur = arr(answers[step.id]).slice();
          if (step.exclusive && id === step.exclusive) {
            /* "None of these" is mutually exclusive with everything else, both ways. */
            cur = cur.indexOf(id) !== -1 ? [] : [id];
          } else {
            if (step.exclusive) cur = cur.filter(function (x) { return x !== step.exclusive; });
            var at = cur.indexOf(id);
            if (at === -1) cur.push(id); else cur.splice(at, 1);
          }
          answers[step.id] = cur;
          view.querySelectorAll('[data-cq-opt]').forEach(function (b) {
            var on = cur.indexOf(b.getAttribute('data-cq-opt')) !== -1;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
          });
          var btn = view.querySelector('[data-cq-next]');
          if (btn) { if (cur.length) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', ''); }
          return;
        }

        answers[step.id] = id;
        view.querySelectorAll('[data-cq-opt]').forEach(function (b) {
          var on = b === o;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });

        if (step.autoAdvance === false) {
          var c = view.querySelector('[data-cq-next]');
          if (c) c.removeAttribute('disabled');
          return;
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { go(index + 1); return; }
        view.classList.add('is-out');
        setTimeout(function () { view.classList.remove('is-out'); go(index + 1); }, 300);
        return;
      }

      var next = e.target.closest('[data-cq-next]');
      if (next) { if (!next.hasAttribute('disabled')) go(index + 1); return; }
      if (e.target.closest('[data-cq-back]')) { back(); return; }
      if (e.target.closest('[data-cq-retake]')) {
        answers = {}; lead = null;
        try { sessionStorage.removeItem(CACHE_KEY); } catch (err) {}
        go(-1);
        return;
      }
    });

    root.addEventListener('submit', function (e) {
      /* S13. The 30 day reflection box. Handled first because it is the only other
         form on the page and it must not fall through to the lead branch. */
      var rform = e.target.closest('[data-cq-rform]');
      if (rform) {
        e.preventDefault();
        var remail = String(new FormData(rform).get('email') || '').trim();
        var rerr = rform.querySelector('[data-cq-rerr]');
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(remail)) {
          rerr.textContent = 'Please add a valid email so the reflection has somewhere to arrive.';
          rerr.hidden = false;
          return;
        }
        rerr.hidden = true;

        var rr = cachedClassify(answers);
        var rpayload = {
          source: 'quiz_result_30day',
          lead: {
            name: String(answers.name || '').trim(),
            email: remail,
            phone: (lead && lead.phone) || ''
          },
          answers: answers,
          reflection_profile: rr.profileTitle,
          associated_charm_mode: rr.modeName,
          mode: rr.mode,
          mode_scores: rr.scores,
          classification_explanation: rr.explanation,
          page: location.pathname
        };
        root.dispatchEvent(new CustomEvent('charm:reflection:signup', { bubbles: true, detail: rpayload }));

        /* NO BACKEND YET. Both of these are empty today. See the header note. */
        var rep = (DATA.results && DATA.results.reflectEndpoint) || (DATA.lead && DATA.lead.endpoint);
        if (rep) {
          fetch(rep, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rpayload)
          }).catch(function () {});
        }

        var w30 = rform.closest('[data-cq-r30]');
        w30.querySelector('[data-cq-r30-box]').hidden = true;
        w30.querySelector('[data-cq-r30-done]').hidden = false;
        return;
      }

      var form = e.target.closest('[data-cq-form]');
      if (!form) return;
      e.preventDefault();
      var fd = new FormData(form);
      var email = (fd.get('email') || '').trim();
      var err = form.querySelector('[data-cq-err]');
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        err.textContent = 'Please add a valid email so we can send your reflection.';
        err.hidden = false;
        return;
      }
      err.hidden = true;
      lead = { name: String(answers.name || '').trim(), email: email, phone: (fd.get('phone') || '').trim() };

      var r = cachedClassify(answers);
      var payload = {
        lead: lead,
        answers: answers,
        reflection_profile: r.profileTitle,
        associated_charm_mode: r.modeName,
        mode: r.mode,
        mode_scores: r.scores,
        classification_explanation: r.explanation,
        page: location.pathname
      };
      root.dispatchEvent(new CustomEvent('charm:quiz:complete', { bubbles: true, detail: payload }));
      if (DATA.lead && DATA.lead.endpoint) {
        fetch(DATA.lead.endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        }).catch(function () {});
      }
      go(RESULT);
    });

    /* Forces any profile for review without completing the quiz:
       ?cq_profile=silenced_self  or  ?cq_profile=truthful_boundaries */
    var force = new URLSearchParams(location.search).get('cq_profile');
    if (force) {
      var aliases = {
        delayed_witness: 'embodied_regulation', emerging_self: 'conscious_evolution',
        silenced_self: 'truthful_boundaries', unmet_voice: 'relational_presence',
        unanchored_creator: 'intentional_living'
      };
      var target = aliases[force] || force;
      if (MODES[target]) {
        cachedClassify = function () {
          var p = MODES[target];
          return { mode: target, profileTitle: p.profileTitle, modeName: p.modeName,
                   scores: zero(), structured: zero(), text: zero(), explanation: 'Forced preview.' };
        };
        answers = { name: 'Preview' };
        go(RESULT);
      }
    }
  }

  function init() { document.querySelectorAll('[data-charm-quiz]').forEach(boot); }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
  document.addEventListener('shopify:section:load', init);
})();
